import React, { useState, useEffect } from 'react'
import { getAllComplaintsByPGID, update_complaint_status } from '../apis/services/pgApi'

const TYPE_ICON = {
  'Water Leakage':     '🚿', 'WiFi Issue':       '📶', 'AC Not Working':   '❄️',
  'Noisy Neighbours':  '🔊', 'Housekeeping':     '🧹', 'Door Lock':        '🔒',
  'Plumbing':          '🔧', 'Electricity':      '⚡', 'Security':         '🛡️',
  'Pest Control':      '🪲', 'Furniture':        '🪑', 'Geyser/Hot Water': '🚿',
  'Lift/Elevator':     '🛗', 'Gas/Cooking':      '🔥', 'Window/Door':      '🪟',
  'Laundry':           '👕', 'Food Quality':     '🍽️', 'Parking':          '🚗',
  'Noise':             '📢', 'Cleanliness':      '🧼', 'Fan':              '💨',
  'Mattress/Bedding':  '🛏️', 'CCTV/Safety':     '📷', 'Drainage':         '🚰',
  'Paint/Walls':       '🎨',
}

// Map API status to component status
const mapStatus = (apiStatus) => {
  const statusLower = (apiStatus || '').toLowerCase();
  if (statusLower === 'resolved') return 'resolved';
  if (statusLower === 'rejected') return 'rejected';
  return 'new'; // Default to new for 'Open' or any other status
}

// Auto priority assignment based on complaint type
const getAutoPriority = (type) => {
  const highPriorityTypes = [
    'Water Leakage', 'Plumbing', 'Electricity', 'AC Not Working', 
    'Door Lock', 'Gas/Cooking', 'Lift/Elevator', 'Geyser/Hot Water',
    'CCTV/Safety', 'Security'
  ];
  
  const moderatePriorityTypes = [
    'WiFi Issue', 'Housekeeping', 'Furniture', 'Pest Control',
    'Window/Door', 'Drainage', 'Paint/Walls'
  ];
  
  const lowPriorityTypes = [
    'Noisy Neighbours', 'Noise', 'Cleanliness', 'Laundry', 
    'Food Quality', 'Parking', 'Mattress/Bedding', 'Fan'
  ];
  
  if (highPriorityTypes.includes(type)) return 'high';
  if (moderatePriorityTypes.includes(type)) return 'moderate';
  if (lowPriorityTypes.includes(type)) return 'low';
  return 'moderate';
}

// Format date to readable format
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Get month/year from date
const getMonthYear = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

const STATUS_CFG = {
  new:      { label: 'Open',      bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE', dot: '#3B82F6' },
  resolved: { label: 'Resolved', bg: '#F0FDF4', text: '#166534', border: '#86EFAC', dot: '#22C55E' },
  rejected: { label: 'Rejected', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA', dot: '#EF4444' },
}

const PRIORITY_CFG = {
  high:     { label: 'High',     color: '#DC2626', bg: '#FFF1F2', border: '#FECACA' },
  moderate: { label: 'Moderate', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  low:      { label: 'Low',      color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC' },
}

const RemarksModal = ({ complaint, onClose }) => (
  <div className="cp-modal-overlay" >
    <div className="cp-modal" onClick={e => e.stopPropagation()}>
      <div className="cp-modal-head">
        <div>
          <h3 className="cp-modal-title">{TYPE_ICON[complaint.ComplaintText] || '📌'} {complaint.ComplaintText}</h3>
          <p className="cp-modal-sub">Tenant ID: {complaint.TenantID} · {formatDate(complaint.ComplaintDate)}</p>
        </div>
        <button className="cp-modal-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="cp-modal-body">
        <p>{complaint.ComplaintText || "No additional details provided"}</p>
      </div>
      <div className="cp-modal-footer">
        <span className="cp-modal-date">{formatDate(complaint.ComplaintDate)}</span>
        <button className="cp-btn cp-btn-close-modal" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
)

const ComplaintRow = ({ complaint, onResolve, onViewRemarks, isResolved }) => {
  const status = mapStatus(complaint.ComplaintStatus)
  const cfg = STATUS_CFG[status]
  const priority = getAutoPriority(complaint.ComplaintText)
  const pcfg = PRIORITY_CFG[priority] || PRIORITY_CFG.moderate

  return (
    <div className="cp-row">

      {/* col 1 — ISSUE */}
      <div className="cp-cell cp-cell-issue">
        <div className="cp-accent" style={{ background: cfg.dot }} />
        <span className="cp-type-icon">{TYPE_ICON[complaint.ComplaintText] || '📌'}</span>
        <span className="cp-type-label">{complaint.ComplaintText}</span>
      </div>

      {/* col 2 — DETAILS */}
      <div className="cp-cell cp-cell-remarks">
        <button className="cp-view-btn" onClick={() => onViewRemarks(complaint)}>View</button>
      </div>

      {/* col 3 — PRIORITY (display only) */}
      <div className="cp-cell cp-cell-priority">
        <span className="cp-priority-badge" style={{ color: pcfg.color, background: pcfg.bg, borderColor: pcfg.border }}>
          {pcfg.label}
        </span>
      </div>

      {/* col 4 — ROOM (placeholder - not in API) */}
      <div className="cp-cell cp-cell-room">
        <span className="cp-room-num">{complaint.RoomNumber}</span>
        {/* <span className="cp-floor-num">—</span> */}
      </div>

      {/* col 5 — TENANT */}
      <div className="cp-cell cp-cell-tenant">{complaint.tenantName}</div>

      {/* col 6 — DATE */}
      <div className="cp-cell cp-cell-date">{formatDate(complaint.ComplaintDate)}</div>

      {/* col 7 — STATUS / ACTION (merged) */}
      <div className="cp-cell cp-cell-status">
        {isResolved ? (
          <span className="cp-status-badge" style={{ background: cfg.bg, color: cfg.text, borderColor: cfg.border }}>
            <span className="cp-status-dot" style={{ background: cfg.dot }} />
            {cfg.label}
          </span>
        ) : (
          <button className="cp-btn cp-btn-resolve" onClick={() => onResolve(complaint.ComplaintID)}>
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Mark Resolved
          </button>
        )}
      </div>

      {/* MOBILE meta row */}
      <div className="cp-meta-row">
        <span className="cp-meta-room">Tenant #{complaint.TenantID}</span>
        <span className="cp-meta-sep">·</span>
        <span className="cp-meta-date">{formatDate(complaint.ComplaintDate)}</span>
        <span className="cp-meta-sep">·</span>
        <span className="cp-priority-pill" style={{ color: pcfg.color }}>{pcfg.label}</span>
      </div>

      {/* MOBILE action area (line 3) — only for active complaints */}
      {!isResolved && (
        <div className="cp-mobile-action">
          <div className="cp-mobile-tools">
            <button className="cp-view-btn" onClick={() => onViewRemarks(complaint)}>View Details</button>
          </div>
          <div className="cp-mobile-btns">
            <button className="cp-btn cp-btn-resolve cp-btn-resolve-full" onClick={() => onResolve(complaint.ComplaintID)}>
              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Mark Resolved
            </button>
          </div>
        </div>
      )}

    </div>
  )
}

const TableHead = () => (
  <div className="cp-row cp-row-head">
    <div className="cp-cell cp-cell-issue">Issue</div>
    <div className="cp-cell cp-cell-remarks">Details</div>
    <div className="cp-cell cp-cell-priority">Priority</div>
    <div className="cp-cell cp-cell-room">Room</div>
    <div className="cp-cell cp-cell-tenant">Tenant</div>
    <div className="cp-cell cp-cell-date">Date</div>
    <div className="cp-cell cp-cell-status">Status</div>
  </div>
)

const HistoryTableHead = () => (
  <div className="cp-row cp-row-head cp-row-head-history">
    <div className="cp-cell cp-cell-issue">Issue</div>
    <div className="cp-cell cp-cell-remarks">Details</div>
    <div className="cp-cell cp-cell-priority">Priority</div>
    <div className="cp-cell cp-cell-room">Room</div>
    <div className="cp-cell cp-cell-tenant">Tenant</div>
    <div className="cp-cell cp-cell-date">Date</div>
    <div className="cp-cell cp-cell-status">Status</div>
  </div>
)

const OwnerComplaints = ({ selectedPG, selectedPGId }) => {
  const [complaints, setComplaints] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openMonths, setOpenMonths] = useState({})
  const [remarksFor, setRemarksFor] = useState(null)
  const [updating, setUpdating] = useState(false)

  // Fetch complaints when PG changes
  useEffect(() => {
    if (selectedPGId) {
      fetchComplaints()
    }
  }, [selectedPGId])

  const fetchComplaints = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getAllComplaintsByPGID(selectedPGId)
      if (response && response.length > 0) {
        setComplaints(response)
        // Auto-open current month in history
        const currentMonth = getMonthYear(new Date().toISOString())
        setOpenMonths({ [currentMonth]: true })
      } else {
        setComplaints([])
      }
    } catch (err) {
      console.error('Error fetching complaints:', err)
      setError('Failed to load complaints')
      setComplaints([])
    } finally {
      setLoading(false)
    }
  }

  const handleResolve = async (complaintId) => {
    setUpdating(true)
    try {
      const response = await update_complaint_status({
        ComplaintID: complaintId,
        ComplaintStatus: 'RESOLVED'
      })
      
      if (response.Success === 1) {
        // Update local state
        setComplaints(prev => prev.map(c => 
          c.ComplaintID === complaintId 
            ? { ...c, ComplaintStatus: 'RESOLVED' }
            : c
        ))
      } else {
        alert(response.Message || 'Failed to update complaint status')
      }
    } catch (err) {
      console.error('Error updating complaint:', err)
      alert('Failed to update complaint status')
    } finally {
      setUpdating(false)
    }
  }

  // Filter complaints
  const active = complaints.filter(c => mapStatus(c.ComplaintStatus) === 'new')
  const resolved = complaints.filter(c => ['resolved', 'rejected'].includes(mapStatus(c.ComplaintStatus)))
  
  // Sort active by date (newest first)
  const sortedActive = [...active].sort((a, b) => 
    new Date(b.ComplaintDate) - new Date(a.ComplaintDate)
  )

  // Group resolved complaints by month
  const resolvedByMonth = {}
  resolved.forEach(c => {
    const month = getMonthYear(c.ComplaintDate)
    if (!resolvedByMonth[month]) resolvedByMonth[month] = []
    resolvedByMonth[month].push(c)
  })
  
  // Sort months by date (newest first)
  const sortedMonths = Object.keys(resolvedByMonth).sort((a, b) => {
    const dateA = new Date(a)
    const dateB = new Date(b)
    return dateB - dateA
  })

  const toggleMonth = (m) => setOpenMonths(p => ({ ...p, [m]: !p[m] }))

  if (loading) {
    return (
      <div className="cp-root">
        <div className="cp-loading">
          <div className="cp-spinner"></div>
          <p>Loading complaints...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="cp-root">
        <div className="cp-error">
          <p>Error: {error}</p>
          <button onClick={fetchComplaints} className="cp-retry-btn">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="cp-root">

      {/* Active Section Header with Stats */}
      <div className="cp-section">
        <div className="cp-section-head">
          <div className="cp-section-title-row">
            <div className="cp-title-with-stats">
              <h3 className="cp-section-title">Active Complaints</h3>
              {active.length > 0 && <span className="cp-count-badge">{active.length}</span>}
            </div>
            <div className="cp-stats">
              <span>Total: <strong>{complaints.length}</strong></span>
              <span className="cp-stat-sep" />
              <span>Open: <strong className="cp-blue">{active.length}</strong></span>
              <span className="cp-stat-sep" />
              <span>Resolved: <strong className="cp-muted">{resolved.length}</strong></span>
            </div>
          </div>
        </div>
        {active.length === 0 ? (
          <div className="cp-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round">
              <path d="M9 12l2 2 4-4"/>
              <circle cx="12" cy="12" r="9"/>
            </svg>
            <p>No active complaints. All clear!</p>
          </div>
        ) : (
          <div className="cp-table">
            <TableHead />
            {sortedActive.map(c => (
              <ComplaintRow 
                key={c.ComplaintID} 
                complaint={c} 
                onResolve={handleResolve}
                onViewRemarks={setRemarksFor} 
                isResolved={false}
                updating={updating}
              />
            ))}
          </div>
        )}
      </div>

      {/* History Section */}
      {sortedMonths.length > 0 && (
        <div className="cp-section">
          <div className="cp-section-head">
            <div className="cp-section-title-row">
              <h3 className="cp-section-title">History</h3>
            </div>
          </div>
          <div className="cp-accordion">
            {sortedMonths.map(month => (
              <div key={month} className="cp-accordion-item">
                <button className="cp-accordion-head" onClick={() => toggleMonth(month)}>
                  <span className="cp-accordion-month">{month} ({resolvedByMonth[month].length})</span>
                  <svg className={`cp-chevron ${openMonths[month] ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </button>
                {openMonths[month] && (
                  <div className="cp-accordion-body">
                    <HistoryTableHead />
                    {resolvedByMonth[month].map(c => (
                      <ComplaintRow 
                        key={c.ComplaintID} 
                        complaint={c} 
                        onResolve={handleResolve}
                        onViewRemarks={setRemarksFor} 
                        isResolved={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {remarksFor && <RemarksModal complaint={remarksFor} onClose={() => setRemarksFor(null)} />}
      
      <style>{`
        .cp-loading, .cp-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
        .cp-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #E5E7EB;
          border-top-color: #6366F1;
          border-radius: 50%;
          animation: cp-spin 0.8s linear infinite;
          margin-bottom: 16px;
        }
        @keyframes cp-spin {
          to { transform: rotate(360deg); }
        }
        .cp-retry-btn {
          padding: 8px 20px;
          background: #6366F1;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          margin-top: 12px;
        }
        .cp-retry-btn:hover {
          background: #4F46E5;
        }
      `}</style>
    </div>
  )
}

export default OwnerComplaints

// import React, { useState } from 'react'

// const TYPE_ICON = {
//   'Water Leakage':     '🚿', 'WiFi Issue':       '📶', 'AC Not Working':   '❄️',
//   'Noisy Neighbours':  '🔊', 'Housekeeping':     '🧹', 'Door Lock':        '🔒',
//   'Plumbing':          '🔧', 'Electricity':      '⚡', 'Security':         '🛡️',
//   'Pest Control':      '🪲', 'Furniture':        '🪑', 'Geyser/Hot Water': '🚿',
//   'Lift/Elevator':     '🛗', 'Gas/Cooking':      '🔥', 'Window/Door':      '🪟',
//   'Laundry':           '👕', 'Food Quality':     '🍽️', 'Parking':          '🚗',
//   'Noise':             '📢', 'Cleanliness':      '🧼', 'Fan':              '💨',
//   'Mattress/Bedding':  '🛏️', 'CCTV/Safety':     '📷', 'Drainage':         '🚰',
//   'Paint/Walls':       '🎨',
// }

// const STATUS_CFG = {
//   new:      { label: 'New',      bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE', dot: '#3B82F6' },
//   resolved: { label: 'Resolved', bg: '#F0FDF4', text: '#166534', border: '#86EFAC', dot: '#22C55E' },
//   rejected: { label: 'Rejected', bg: '#FFF1F2', text: '#991B1B', border: '#FECACA', dot: '#EF4444' },
// }

// const PRIORITY_CFG = {
//   high:     { label: 'High',     color: '#DC2626', bg: '#FFF1F2', border: '#FECACA' },
//   moderate: { label: 'Moderate', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
//   low:      { label: 'Low',      color: '#16A34A', bg: '#F0FDF4', border: '#86EFAC' },
// }

// // Auto priority assignment based on complaint type
// const getAutoPriority = (type) => {
//   const highPriorityTypes = [
//     'Water Leakage', 'Plumbing', 'Electricity', 'AC Not Working', 
//     'Door Lock', 'Gas/Cooking', 'Lift/Elevator', 'Geyser/Hot Water',
//     'CCTV/Safety', 'Security'
//   ];
  
//   const moderatePriorityTypes = [
//     'WiFi Issue', 'Housekeeping', 'Furniture', 'Pest Control',
//     'Window/Door', 'Drainage', 'Paint/Walls'
//   ];
  
//   const lowPriorityTypes = [
//     'Noisy Neighbours', 'Noise', 'Cleanliness', 'Laundry', 
//     'Food Quality', 'Parking', 'Mattress/Bedding', 'Fan'
//   ];
  
//   if (highPriorityTypes.includes(type)) return 'high';
//   if (moderatePriorityTypes.includes(type)) return 'moderate';
//   if (lowPriorityTypes.includes(type)) return 'low';
//   return 'moderate'; // default
// }

// const INITIAL_COMPLAINTS = [
//   { id: 1,  tenant: 'Rahul S.',  room: '101', floor: 1, type: 'Water Leakage',   status: 'new',      priority: getAutoPriority('Water Leakage'),   month: 'March 2026',    date: 'Mar 18', desc: 'Pipe leaking near bathroom sink since 2 days. Water seeping into the floor.' },
//   { id: 2,  tenant: 'Sneha R.',  room: '103', floor: 1, type: 'WiFi Issue',       status: 'new',      priority: getAutoPriority('WiFi Issue'),       month: 'March 2026',    date: 'Mar 20', desc: 'Internet not working in the room for 3 days. Router shows red light.' },
//   { id: 3,  tenant: 'Pooja G.',  room: '201', floor: 2, type: 'AC Not Working',   status: 'new',      priority: getAutoPriority('AC Not Working'),   month: 'March 2026',    date: 'Mar 21', desc: 'AC stopped cooling. Room temperature very high. Unable to sleep.' },
//   { id: 4,  tenant: 'Arjun C.',  room: '202', floor: 2, type: 'Noisy Neighbours', status: 'new',      priority: getAutoPriority('Noisy Neighbours'), month: 'March 2026',    date: 'Mar 22', desc: '' },
//   { id: 5,  tenant: 'Manish V.', room: '205', floor: 2, type: 'Housekeeping',     status: 'new',      priority: getAutoPriority('Housekeeping'),     month: 'March 2026',    date: 'Mar 22', desc: 'Room not cleaned for 4 days. Dustbin overflowing.' },
//   { id: 6,  tenant: 'Amit K.',   room: '102', floor: 1, type: 'Door Lock',        status: 'resolved', priority: getAutoPriority('Door Lock'),        month: 'March 2026',    date: 'Mar 10', desc: 'Door lock broken and not secure.' },
//   { id: 7,  tenant: 'Kavita F.', room: '204', floor: 2, type: 'Plumbing',         status: 'resolved', priority: getAutoPriority('Plumbing'),         month: 'March 2026',    date: 'Mar 12', desc: '' },
//   { id: 13, tenant: 'Geeta Q.',  room: '304', floor: 3, type: 'Furniture',        status: 'resolved', priority: getAutoPriority('Furniture'),        month: 'January 2026',  date: 'Jan 22', desc: 'Bed frame broken. One side collapsed.' },
// ]

// const MONTH_ORDER = ['March 2026', 'February 2026', 'January 2026']

// const RemarksModal = ({ c, onClose }) => (
//   <div className="cp-modal-overlay" >
//     <div className="cp-modal" onClick={e => e.stopPropagation()}>
//       <div className="cp-modal-head">
//         <div>
//           <h3 className="cp-modal-title">{TYPE_ICON[c.type] || '📌'} {c.type}</h3>
//           <p className="cp-modal-sub">Room {c.room} · Floor {c.floor} · {c.tenant}</p>
//         </div>
//         <button className="cp-modal-close" onClick={onClose}>
//           <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
//             <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
//           </svg>
//         </button>
//       </div>
//       <div className="cp-modal-body"><p>{c.desc}</p></div>
//       <div className="cp-modal-footer">
//         <span className="cp-modal-date">{c.date}</span>
//         <button className="cp-btn cp-btn-close-modal" onClick={onClose}>Close</button>
//       </div>
//     </div>
//   </div>
// )

// const ComplaintRow = ({ c, onAct, onViewRemarks, isResolved }) => {
//   const cfg  = STATUS_CFG[c.status]
//   const pcfg = PRIORITY_CFG[c.priority] || PRIORITY_CFG.moderate

//   return (
//     <div className="cp-row">

//       {/* col 1 — ISSUE */}
//       <div className="cp-cell cp-cell-issue">
//         <div className="cp-accent" style={{ background: cfg.dot }} />
//         <span className="cp-type-icon">{TYPE_ICON[c.type] || '📌'}</span>
//         <span className="cp-type-label">{c.type}</span>
//       </div>

//       {/* col 2 — DETAILS */}
//       <div className="cp-cell cp-cell-remarks">
//         {c.desc
//           ? <button className="cp-view-btn" onClick={() => onViewRemarks(c)}>View</button>
//           : <span className="cp-no-remarks">—</span>}
//       </div>

//       {/* col 3 — PRIORITY (display only) */}
//       <div className="cp-cell cp-cell-priority">
//         <span className="cp-priority-badge" style={{ color: pcfg.color, background: pcfg.bg, borderColor: pcfg.border }}>
//           {pcfg.label}
//         </span>
//       </div>

//       {/* col 4 — ROOM */}
//       <div className="cp-cell cp-cell-room">
//         <span className="cp-room-num">Room {c.room}</span>
//         <span className="cp-floor-num">Floor {c.floor}</span>
//       </div>

//       {/* col 5 — TENANT */}
//       <div className="cp-cell cp-cell-tenant">{c.tenant}</div>

//       {/* col 6 — DATE */}
//       <div className="cp-cell cp-cell-date">{c.date}</div>

//       {/* col 7 — STATUS / ACTION (merged) */}
//       <div className="cp-cell cp-cell-status">
//         {isResolved ? (
//           <span className="cp-status-badge" style={{ background: cfg.bg, color: cfg.text, borderColor: cfg.border }}>
//             <span className="cp-status-dot" style={{ background: cfg.dot }} />
//             {cfg.label}
//           </span>
//         ) : (
//           <button className="cp-btn cp-btn-resolve" onClick={() => onAct(c.id, 'resolved')}>
//             <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
//               <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
//             </svg>
//             Mark Resolved
//           </button>
//         )}
//       </div>

//       {/* MOBILE meta row — room · tenant · date · priority (line 2) */}
//       <div className="cp-meta-row">
//         <span className="cp-meta-room">Room {c.room}</span>
//         <span className="cp-meta-sep">·</span>
//         <span className="cp-meta-tenant">{c.tenant}</span>
//         <span className="cp-meta-sep">·</span>
//         <span className="cp-meta-date">{c.date}</span>
//         <span className="cp-meta-sep">·</span>
//         <span className="cp-priority-pill" style={{ color: pcfg.color }}>{pcfg.label}</span>
//       </div>

//       {/* MOBILE action area (line 3) — only for active complaints */}
//       {!isResolved && (
//         <div className="cp-mobile-action">
//           <div className="cp-mobile-tools">
//             {c.desc && (
//               <button className="cp-view-btn" onClick={() => onViewRemarks(c)}>View Details</button>
//             )}
//           </div>
//           <div className="cp-mobile-btns">
//             <button className="cp-btn cp-btn-resolve cp-btn-resolve-full" onClick={() => onAct(c.id, 'resolved')}>
//               <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
//                 <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
//               </svg>
//               Mark Resolved
//             </button>
//           </div>
//         </div>
//       )}

//     </div>
//   )
// }

// const TableHead = () => (
//   <div className="cp-row cp-row-head">
//     <div className="cp-cell cp-cell-issue">Issue</div>
//     <div className="cp-cell cp-cell-remarks">Details</div>
//     <div className="cp-cell cp-cell-priority">Priority</div>
//     <div className="cp-cell cp-cell-room">Room</div>
//     <div className="cp-cell cp-cell-tenant">Tenant</div>
//     <div className="cp-cell cp-cell-date">Date</div>
//     <div className="cp-cell cp-cell-status">Status</div>
//   </div>
// )

// const HistoryTableHead = () => (
//   <div className="cp-row cp-row-head cp-row-head-history">
//     <div className="cp-cell cp-cell-issue">Issue</div>
//     <div className="cp-cell cp-cell-remarks">Details</div>
//     <div className="cp-cell cp-cell-priority">Priority</div>
//     <div className="cp-cell cp-cell-room">Room</div>
//     <div className="cp-cell cp-cell-tenant">Tenant</div>
//     <div className="cp-cell cp-cell-date">Date</div>
//     <div className="cp-cell cp-cell-status">Status</div>
//   </div>
// )

// const OwnerComplaints = () => {
//   const [complaints, setComplaints] = useState(INITIAL_COMPLAINTS)
//   const [openMonths, setOpenMonths] = useState({ 'March 2026': true })
//   const [remarksFor, setRemarksFor] = useState(null)

//   const active   = complaints.filter(c => c.status === 'new')
//   const resolved = complaints.filter(c => ['resolved','rejected'].includes(c.status))
//   const sortedActive = [...active].sort((a, b) => a.id - b.id)

//   const resolvedByMonth = {}
//   resolved.forEach(c => { if (!resolvedByMonth[c.month]) resolvedByMonth[c.month] = []; resolvedByMonth[c.month].push(c) })
//   const sortedMonths = Object.keys(resolvedByMonth).sort((a, b) => MONTH_ORDER.indexOf(a) - MONTH_ORDER.indexOf(b))

//   const toggleMonth = m => setOpenMonths(p => ({ ...p, [m]: !p[m] }))
//   const act         = (id, s) => setComplaints(p => p.map(c => c.id === id ? { ...c, status: s } : c))

//   return (
//     <div className="cp-root">

//       {/* Active Section Header with Stats */}
//       <div className="cp-section">
//         <div className="cp-section-head">
//           <div className="cp-section-title-row">
//             <div className="cp-title-with-stats">
//               <h3 className="cp-section-title">Active Complaints</h3>
//               {active.length > 0 && <span className="cp-count-badge">{active.length}</span>}
//             </div>
//             <div className="cp-stats">
//               <span>Total: <strong>{complaints.length}</strong></span>
//               <span className="cp-stat-sep" />
//               <span>New: <strong className="cp-blue">{complaints.filter(c=>c.status==='new').length}</strong></span>
//               <span className="cp-stat-sep" />
//               <span>Resolved: <strong className="cp-muted">{complaints.filter(c=>c.status==='resolved').length}</strong></span>
//             </div>
//           </div>
//         </div>
//         {active.length === 0 ? (
//           <div className="cp-empty">
//             <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="9"/></svg>
//             <p>No active complaints. All clear!</p>
//           </div>
//         ) : (
//           <div className="cp-table">
//             <TableHead />
//             {sortedActive.map(c => <ComplaintRow key={c.id} c={c} onAct={act} onViewRemarks={setRemarksFor} isResolved={false} />)}
//           </div>
//         )}
//       </div>

//       {/* History */}
//       <div className="cp-section">
//         <div className="cp-section-head">
//           <div className="cp-section-title-row">
//             <h3 className="cp-section-title">History</h3>
//           </div>
//         </div>
//         <div className="cp-accordion">
//           {sortedMonths.map(month => (
//             <div key={month} className="cp-accordion-item">
//               <button className="cp-accordion-head" onClick={() => toggleMonth(month)}>
//                 <span className="cp-accordion-month">{month} ({resolvedByMonth[month].length})</span>
//                 <svg className={`cp-chevron ${openMonths[month] ? 'open' : ''}`} width="14" height="14" viewBox="0 0 14 14" fill="none">
//                   <path d="M3 5L7 9L11 5" stroke="#9CA3AF" strokeWidth="1.6" strokeLinecap="round"/>
//                 </svg>
//               </button>
//               {openMonths[month] && (
//                 <div className="cp-accordion-body">
//                   <HistoryTableHead />
//                   {resolvedByMonth[month].map(c => <ComplaintRow key={c.id} c={c} onAct={act} onViewRemarks={setRemarksFor} isResolved={true} />)}
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       {remarksFor && <RemarksModal c={remarksFor} onClose={() => setRemarksFor(null)} />}
//     </div>
//   )
// }

// export default OwnerComplaints